/**
 * @file page.tsx
 * @description 개인정보 처리방침 페이지
 *
 * BIDIX 개인정보처리방침을 표시하는 페이지입니다.
 * Sticky TOC 레이아웃과 시뮬레이션 데이터 관련 조항을 포함한 전문적인 문서 스타일입니다.
 *
 * 주요 기능:
 * 1. 좌측 고정 목차 (Sticky TOC)
 * 2. 시뮬레이션 데이터 수집 항목 강조 (Indigo Box)
 * 3. 인쇄 지원 (no-print 클래스)
 *
 * @dependencies
 * - next/link: 내부 링크 네비게이션
 * - lucide-react: 아이콘
 * - @/components/shared/ui/button: 버튼 컴포넌트
 */

"use client";

import { Button } from "@/components/shared/ui/button";
import { Printer, Database } from "lucide-react";

export default function PrivacyPage() {
  const handlePrint = () => {
    if (typeof window !== "undefined") {
      window.print();
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50 dark:bg-zinc-950 text-zinc-900 dark:text-zinc-100">
      <div className="max-w-7xl mx-auto p-4 sm:p-6 lg:py-12 flex flex-col lg:flex-row gap-6 sm:gap-8 lg:gap-12">
        {/* [LEFT] Sticky Navigation (목차) */}
        <aside className="hidden lg:block w-64 flex-shrink-0 no-print">
          <div className="sticky top-24">
            <h3 className="text-xs font-bold text-zinc-400 dark:text-zinc-500 uppercase tracking-wider mb-4">
              Privacy Policy
            </h3>
            <nav className="space-y-1 border-l border-zinc-200 dark:border-zinc-800">
              <a
                href="#article1"
                className="block pl-4 py-2 text-sm text-zinc-500 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100 hover:border-l-2 hover:border-zinc-900 dark:hover:border-zinc-100 transition-all -ml-[2px]"
              >
                1. 처리목적
              </a>
              <a
                href="#article2"
                className="block pl-4 py-2 text-sm text-zinc-500 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100 hover:border-l-2 hover:border-zinc-900 dark:hover:border-zinc-100 transition-all -ml-[2px]"
              >
                2. 처리 및 보유기간
              </a>
              <a
                href="#article3"
                className="block pl-4 py-2 text-sm text-zinc-500 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100 hover:border-l-2 hover:border-zinc-900 dark:hover:border-zinc-100 transition-all -ml-[2px]"
              >
                3. 처리하는 항목
              </a>
              <a
                href="#article4"
                className="block pl-4 py-2 text-sm text-zinc-500 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100 hover:border-l-2 hover:border-zinc-900 dark:hover:border-zinc-100 transition-all -ml-[2px]"
              >
                4. 업무 위탁
              </a>
              <a
                href="#article5"
                className="block pl-4 py-2 text-sm text-zinc-500 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100 hover:border-l-2 hover:border-zinc-900 dark:hover:border-zinc-100 transition-all -ml-[2px]"
              >
                5. 이용자의 권리
              </a>
              <a
                href="#article6"
                className="block pl-4 py-2 text-sm text-zinc-500 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100 hover:border-l-2 hover:border-zinc-900 dark:hover:border-zinc-100 transition-all -ml-[2px]"
              >
                6. 안전성 확보조치
              </a>
            </nav>
          </div>
        </aside>

        {/* [RIGHT] Main Content */}
        <main className="flex-1 bg-white dark:bg-zinc-900 rounded-xl sm:rounded-2xl shadow-sm border border-zinc-200 dark:border-zinc-800 p-6 sm:p-8 md:p-12 print:shadow-none print:border-none print:rounded-none print:p-0">
          <header className="mb-8 sm:mb-12 border-b-2 border-zinc-900 dark:border-zinc-100 pb-4 sm:pb-6">
            <div className="flex flex-col sm:flex-row items-start justify-between gap-4 mb-4">
              <div className="flex-1">
                <h1 className="text-2xl sm:text-3xl md:text-4xl font-extrabold mb-3 sm:mb-4 tracking-tight text-zinc-900 dark:text-zinc-100">
                  개인정보 처리방침
                </h1>
                <p className="text-zinc-500 dark:text-zinc-400 text-xs sm:text-sm leading-relaxed">
                  BIDIX(이하 &quot;회사&quot;)는 이용자의 개인정보를 소중히
                  다루며, 「개인정보 보호법」 등 관련 법령을 준수합니다.
                  <br className="hidden sm:block" />본 방침은 2026년 2월 1일부터 시행됩니다.
                </p>
              </div>
              <div className="no-print self-start sm:self-auto">
                <Button
                  onClick={handlePrint}
                  variant="ghost"
                  className="text-zinc-500 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100 text-xs sm:text-sm font-medium flex items-center gap-2 px-3 py-2 rounded hover:bg-zinc-100 dark:hover:bg-zinc-800 transition"
                >
                  <Printer className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  <span className="hidden sm:inline">인쇄하기</span>
                  <span className="sm:hidden">인쇄</span>
                </Button>
              </div>
            </div>
          </header>

          <div className="space-y-8 sm:space-y-12 text-xs sm:text-sm leading-relaxed text-zinc-700 dark:text-zinc-300">
            {/* 제1조 처리목적 */}
            <section id="article1" className="scroll-mt-24">
              <h2 className="text-base sm:text-lg font-bold text-zinc-900 dark:text-zinc-100 mb-3 sm:mb-4 flex items-center gap-2">
                <span className="w-1.5 h-5 bg-zinc-900 dark:bg-zinc-100 rounded-full flex-shrink-0"></span>{" "}
                제1조 (개인정보의 처리목적)
              </h2>
              <p className="mb-3 sm:mb-4 text-zinc-700 dark:text-zinc-300">
                회사는 다음의 목적을 위하여 개인정보를 처리합니다. 처리하고 있는
                개인정보는 다음의 목적 이외의 용도로는 이용되지 않습니다.
              </p>
              <ul className="list-disc list-inside space-y-2 pl-2 marker:text-zinc-400 dark:marker:text-zinc-600">
                <li>
                  <strong className="text-zinc-900 dark:text-zinc-100">회원 가입 및 관리:</strong> 본인 식별·인증, 회원자격
                  유지·관리, 서비스 부정이용 방지
                </li>
                <li>
                  <strong className="text-zinc-900 dark:text-zinc-100">서비스 제공:</strong> 사건 검토 기록 및 결과 저장,
                  맞춤형 리포트 생성, 이용 기록 분석
                </li>
                <li>
                  <strong className="text-zinc-900 dark:text-zinc-100">고충 처리:</strong> 민원인의 신원 확인, 민원사항 확인,
                  사실조사를 위한 연락·통지, 처리결과 통보
                </li>
              </ul>
            </section>

            <hr className="border-zinc-100 dark:border-zinc-800" />

            {/* 제2조 보유기간 */}
            <section id="article2" className="scroll-mt-24">
              <h2 className="text-base sm:text-lg font-bold text-zinc-900 dark:text-zinc-100 mb-3 sm:mb-4 flex items-center gap-2">
                <span className="w-1.5 h-5 bg-zinc-900 dark:bg-zinc-100 rounded-full flex-shrink-0"></span>{" "}
                제2조 (개인정보의 처리 및 보유기간)
              </h2>
              <div className="bg-zinc-50 dark:bg-zinc-800/50 p-4 rounded-lg border border-zinc-200 dark:border-zinc-800">
                <ul className="list-disc list-inside space-y-2 text-zinc-700 dark:text-zinc-300">
                  <li>
                    <strong className="text-zinc-900 dark:text-zinc-100">회원 정보:</strong> 회원 탈퇴 시까지 (단, 관계 법령
                    위반에 따른 수사 시 종료 시까지)
                  </li>
                  <li>
                    <strong className="text-zinc-900 dark:text-zinc-100">계약 및 청약철회 기록:</strong> 5년 (전자상거래법)
                  </li>
                  <li>
                    <strong className="text-zinc-900 dark:text-zinc-100">대금결제 및 재화 공급 기록:</strong> 5년
                    (전자상거래법)
                  </li>
                  <li>
                    <strong className="text-zinc-900 dark:text-zinc-100">소비자 불만/분쟁 처리 기록:</strong> 3년
                    (전자상거래법)
                  </li>
                  <li>
                    <strong className="text-zinc-900 dark:text-zinc-100">웹사이트 방문 기록:</strong> 3개월 (통신비밀보호법)
                  </li>
                </ul>
              </div>
            </section>

            <hr className="border-zinc-100 dark:border-zinc-800" />

            {/* 제3조 처리항목 (중요 업데이트) */}
            <section id="article3" className="scroll-mt-24">
              <h2 className="text-base sm:text-lg font-bold text-zinc-900 dark:text-zinc-100 mb-3 sm:mb-4 flex items-center gap-2">
                <span className="w-1.5 h-5 bg-zinc-900 dark:bg-zinc-100 rounded-full flex-shrink-0"></span>{" "}
                제3조 (처리하는 개인정보의 항목)
              </h2>
              <div className="space-y-3 sm:space-y-4">
                <div>
                  <h3 className="font-bold text-zinc-800 dark:text-zinc-200 mb-2 text-sm sm:text-base">
                    1. 회원 가입 시
                  </h3>
                  <p className="pl-2 text-zinc-600 dark:text-zinc-400 text-xs sm:text-sm">
                    - 필수항목: 이메일, 비밀번호, 이름(또는 닉네임)
                  </p>
                  <p className="pl-2 text-zinc-600 dark:text-zinc-400 text-xs sm:text-sm">
                    - 선택항목: 프로필 이미지
                  </p>
                </div>
                <div className="bg-indigo-50 dark:bg-indigo-950/30 p-4 rounded-lg border border-indigo-100 dark:border-indigo-900/50">
                  <h3 className="font-bold text-indigo-900 dark:text-indigo-200 mb-2 flex items-center gap-2 text-sm sm:text-base">
                    <Database className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    2. 서비스 이용 과정 (시뮬레이션 데이터)
                  </h3>
                  <p className="text-indigo-800/80 dark:text-indigo-300/80 mb-2 text-xs sm:text-sm">
                    맞춤형 분석 리포트 제공 및 이력 저장을 위해 다음 정보가
                    수집됩니다.
                  </p>
                  <ul className="list-disc list-inside pl-2 text-indigo-700 dark:text-indigo-300 space-y-1 text-xs sm:text-sm">
                    <li>
                      사용자가 입력한{" "}
                      <strong className="text-indigo-900 dark:text-indigo-100">입찰 희망가, 매도 예상가, 자금 보유 현황</strong>
                    </li>
                    <li>
                      사용자가 체크한{" "}
                      <strong className="text-indigo-900 dark:text-indigo-100">권리 분석 내역, 위험 요소 체크리스트</strong>
                    </li>
                    <li>시뮬레이션 결과값 및 생성 일시</li>
                  </ul>
                </div>
                <div>
                  <h3 className="font-bold text-zinc-800 dark:text-zinc-200 mb-2 text-sm sm:text-base">
                    3. 자동 수집 항목
                  </h3>
                  <p className="pl-2 text-zinc-600 dark:text-zinc-400 text-xs sm:text-sm">
                    - IP주소, 쿠키, 서비스 이용 기록, 기기 정보
                  </p>
                </div>
              </div>
            </section>

            <hr className="border-zinc-100 dark:border-zinc-800" />

            {/* 제4조 위탁 */}
            <section id="article4" className="scroll-mt-24">
              <h2 className="text-base sm:text-lg font-bold text-zinc-900 dark:text-zinc-100 mb-3 sm:mb-4 flex items-center gap-2">
                <span className="w-1.5 h-5 bg-zinc-900 dark:bg-zinc-100 rounded-full flex-shrink-0"></span>{" "}
                제4조 (개인정보처리의 위탁)
              </h2>
              <p className="mb-3 sm:mb-4 text-zinc-700 dark:text-zinc-300">
                회사는 원활한 개인정보 업무처리를 위하여 다음과 같이 개인정보
                처리업무를 위탁하고 있습니다.
              </p>
              <div className="overflow-x-auto -mx-4 sm:mx-0">
                <div className="overflow-hidden rounded-lg border border-zinc-200 dark:border-zinc-800 min-w-full sm:min-w-0">
                  <table className="w-full text-xs sm:text-sm text-left">
                    <thead className="bg-zinc-50 dark:bg-zinc-800/50 font-bold text-zinc-500 dark:text-zinc-400">
                      <tr>
                        <th className="p-2 sm:p-3 border-b border-zinc-200 dark:border-zinc-800">
                          수탁 업체
                        </th>
                        <th className="p-2 sm:p-3 border-b border-zinc-200 dark:border-zinc-800">
                          위탁 업무 내용
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-zinc-100 dark:divide-zinc-800">
                      <tr>
                        <td className="p-2 sm:p-3 text-zinc-900 dark:text-zinc-100 font-medium">Clerk</td>
                        <td className="p-2 sm:p-3 text-zinc-600 dark:text-zinc-400">
                          회원 가입, 로그인 인증 및 계정 보안 관리
                        </td>
                      </tr>
                      <tr>
                        <td className="p-2 sm:p-3 text-zinc-900 dark:text-zinc-100 font-medium">Supabase</td>
                        <td className="p-2 sm:p-3 text-zinc-600 dark:text-zinc-400">
                          데이터베이스 호스팅, 시뮬레이션 데이터 저장 및 관리
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              <p className="mt-3 sm:mt-4 text-zinc-600 dark:text-zinc-400 text-[10px] sm:text-xs">
                회사는 위탁계약 체결 시 「개인정보 보호법」 제26조에 따라
                위탁업무 수행목적 외 개인정보 처리금지, 기술적·관리적 보호조치,
                재위탁 제한, 수탁자에 대한 관리·감독, 손해배상 등 책임에 관한
                사항을 계약서 등 문서에 명시하고, 수탁자가 개인정보를 안전하게
                처리하는지를 감독하고 있습니다.
              </p>
            </section>

            <hr className="border-zinc-100 dark:border-zinc-800" />

            {/* 제5조 이용자의 권리 */}
            <section id="article5" className="scroll-mt-24">
              <h2 className="text-base sm:text-lg font-bold text-zinc-900 dark:text-zinc-100 mb-3 sm:mb-4">
                제5조 (이용자의 권리·의무 및 행사방법)
              </h2>
              <p className="mb-3 sm:mb-4 text-zinc-700 dark:text-zinc-300">
                이용자는 회사에 대해 언제든지 개인정보 열람, 정정, 삭제,
                처리정지 요구 등의 권리를 행사할 수 있습니다. 권리 행사는 서면,
                전자우편 등을 통하여 하실 수 있으며 회사는 이에 대해 지체 없이
                조치하겠습니다.
              </p>
              <p className="text-zinc-600 dark:text-zinc-400">
                이용자가 개인정보의 오류 등에 대한 정정 또는 삭제를 요구한
                경우에는 회사는 지체 없이 그 오류 등을 정정하거나 삭제한 후 당해
                개인정보를 제공받은 자에게 지체 없이 통지하여 정정 또는 삭제
                조치를 하겠습니다.
              </p>
            </section>

            <hr className="border-zinc-100 dark:border-zinc-800" />

            {/* 제6조 안전성 확보조치 */}
            <section id="article6" className="scroll-mt-24">
              <h2 className="text-base sm:text-lg font-bold text-zinc-900 dark:text-zinc-100 mb-3 sm:mb-4">
                제6조 (개인정보의 안전성 확보조치)
              </h2>
              <ul className="list-disc list-inside space-y-2 text-zinc-600 dark:text-zinc-400 pl-2">
                <li>
                  <strong className="text-zinc-900 dark:text-zinc-100">관리적 조치:</strong> 내부관리계획 수립·시행, 정기적
                  직원 교육
                </li>
                <li>
                  <strong className="text-zinc-900 dark:text-zinc-100">기술적 조치:</strong> 개인정보처리시스템 접근권한
                  관리, 접근통제시스템 설치, 고유식별정보 등의 암호화,
                  보안프로그램 설치
                </li>
                <li>
                  <strong className="text-zinc-900 dark:text-zinc-100">물리적 조치:</strong> 전산실, 자료보관실 등의 접근통제
                </li>
              </ul>
            </section>

            <hr className="border-zinc-200 dark:border-zinc-800" />

            {/* 개인정보 보호책임자 */}
            <section className="bg-zinc-50 dark:bg-zinc-800/50 p-5 sm:p-6 rounded-xl border border-zinc-200 dark:border-zinc-800 mt-6 sm:mt-8">
              <h3 className="font-bold text-zinc-900 dark:text-zinc-100 mb-3 sm:mb-4 text-base sm:text-lg">
                개인정보 보호책임자
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 text-xs sm:text-sm">
                <div>
                  <span className="text-zinc-500 dark:text-zinc-400 block mb-1">성명 / 직책</span>
                  <span className="font-medium text-zinc-900 dark:text-zinc-100">구재현 / 대표</span>
                </div>
                <div>
                  <span className="text-zinc-500 dark:text-zinc-400 block mb-1">연락처</span>
                  <span className="font-medium text-zinc-900 dark:text-zinc-100 break-all">bidix.hq@gmail.com</span>
                </div>
              </div>
              <p className="mt-3 sm:mt-4 text-[10px] sm:text-xs text-zinc-600 dark:text-zinc-400">
                이용자께서는 회사의 서비스를 이용하시면서 발생한 모든 개인정보
                보호 관련 문의, 불만처리, 피해구제 등에 관한 사항을 개인정보
                보호책임자에게 문의하실 수 있습니다. 회사는 이용자의 문의에 대해
                지체 없이 답변 및 처리해드릴 것입니다.
              </p>
            </section>
          </div>
        </main>
      </div>
    </div>
  );
}
